import { useState } from "react";
import {
  useGetList,
  useCreate,
  useUpdate,
  useDelete,
  useNotify,
  type Identifier,
} from "ra-core";

import { Button } from "@/components/ui/button";
import { Confirm } from "@/components/admin/confirm";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { WorkbenchFilters } from "./loadWorkbenchUtils";

export type WorkbenchSavedView = {
  id: Identifier;
  sales_id: Identifier;
  name: string;
  filters: WorkbenchFilters;
};
type SavedViewsProps = {
  userId?: Identifier;
  filters: WorkbenchFilters;
  valid: boolean;
  onSelect: (filters: WorkbenchFilters) => void;
};
const resource = "workbench_saved_views";

export const WorkbenchSavedViews = ({
  userId,
  filters,
  valid,
  onSelect,
}: SavedViewsProps) => {
  const {
    data: views = [],
    isPending,
    error,
    refetch,
  } = useGetList<WorkbenchSavedView>(
    resource,
    {
      pagination: { page: 1, perPage: 1000 },
      sort: { field: "name", order: "ASC" },
      filter: { "sales_id@eq": userId },
    },
    { enabled: userId != null, refetchOnWindowFocus: "always" },
  );
  const [create, { isPending: creating }] = useCreate<WorkbenchSavedView>();
  const [update, { isPending: updating }] = useUpdate<WorkbenchSavedView>();
  const [remove, { isPending: removing }] = useDelete<WorkbenchSavedView>();
  const notify = useNotify();
  const [name, setName] = useState("");
  const [pendingDelete, setPendingDelete] = useState<WorkbenchSavedView | null>(
    null,
  );
  const busy = creating || updating || removing;
  const unavailable = userId == null || isPending || !!error;
  const selected = views.find(({ filters: saved }) =>
    (Object.keys(filters) as (keyof WorkbenchFilters)[]).every(
      (key) => saved[key] === filters[key],
    ),
  );

  const save = async () => {
    const trimmed = name.trim();
    if (!trimmed || unavailable || busy || !valid) return;
    const existing = views.find((view) => view.name === trimmed);
    try {
      if (existing) {
        await update(
          resource,
          {
            id: existing.id,
            data: { name: trimmed, filters },
            previousData: existing,
          },
          { returnPromise: true, mutationMode: "pessimistic" },
        );
      } else {
        await create(
          resource,
          { data: { name: trimmed, filters, sales_id: userId } },
          { returnPromise: true },
        );
      }
      notify("Saved view is available on your other devices.", {
        type: "success",
      });
    } catch {
      notify(
        "Unable to save view. Refresh and try again; that name may already exist.",
        { type: "error" },
      );
    }
  };
  const deleteView = async () => {
    if (!pendingDelete || unavailable || busy) return;
    try {
      await remove(
        resource,
        { id: pendingDelete.id, previousData: pendingDelete },
        { returnPromise: true, mutationMode: "pessimistic" },
      );
      setPendingDelete(null);
      notify("Saved view removed.", { type: "success" });
    } catch {
      notify("Unable to remove saved view.", { type: "error" });
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-end gap-2">
        <div className="w-full space-y-1.5 sm:w-64">
          <span className="text-xs font-medium text-muted-foreground">
            Saved views (all your devices)
          </span>
          <Select
            value={selected ? String(selected.id) : ""}
            disabled={unavailable || busy || !views.length}
            onValueChange={(value) => {
              const view = views.find(({ id }) => String(id) === value);
              if (view) {
                setName(view.name);
                onSelect(view.filters);
              }
            }}
          >
            <SelectTrigger className="w-full" aria-label="Saved views">
              <SelectValue
                placeholder={
                  isPending && userId != null
                    ? "Loading saved views…"
                    : "Choose a saved view"
                }
              />
            </SelectTrigger>
            <SelectContent>
              {views.map(({ id, name }) => (
                <SelectItem key={id} value={String(id)}>
                  {name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Input
          className="w-full sm:w-56"
          aria-label="Saved view name"
          placeholder="e.g. My logistics this month"
          maxLength={80}
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
        <Button
          variant="outline"
          disabled={unavailable || busy || !name.trim() || !valid}
          onClick={save}
        >
          {views.some((view) => view.name === name.trim())
            ? "Update saved view"
            : "Save view"}
        </Button>
        {selected && (
          <Button
            variant="ghost"
            disabled={unavailable || busy}
            onClick={() => setPendingDelete(selected)}
          >
            Remove saved view
          </Button>
        )}
      </div>
      <Confirm
        isOpen={pendingDelete != null}
        title="Remove saved view?"
        content={`Remove “${pendingDelete?.name ?? ""}” from your saved views on all devices? This does not delete any loads.`}
        confirm="Remove saved view"
        confirmColor="warning"
        loading={busy}
        onConfirm={deleteView}
        onClose={() => {
          if (!busy) setPendingDelete(null);
        }}
      />
      {error && (
        <p role="alert" className="text-sm text-destructive">
          Unable to load saved views.{" "}
          <button className="underline" onClick={() => void refetch()}>
            Retry
          </button>
        </p>
      )}
    </div>
  );
};
